<template>
  <div class="accept-invite pa-10 d-flex">
    <div class="w-75">
      <div class="d-flex justify-center">
        <v-img eager style="max-width: 60%" class="mb-8" src="@/assets/logo_cockpit_full_svg.svg" />
      </div>

      <div v-if="loading" class="text-center pa-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <v-alert v-else-if="!resolved.valid" type="error" variant="tonal" border="start">
        {{ invalidReasonText }}
        <!-- Logged in with an account other than the invited e-mail: the way out is that account. -->
        <div v-if="resolved.reason === 'wrong_email'" class="mt-4">
          <v-btn variant="outlined" @click="switchAccount">
            {{ getTranslation('$vuetify.investorInvite.switchAccount') }}
          </v-btn>
        </div>
      </v-alert>

      <v-card v-else-if="!authenticated" theme="dark" elevation="0" class="pa-6 invite-card">
        <v-card-title class="text-h6">
          {{ getTranslation('$vuetify.investorInvite.title', resolved.client_name) }}
        </v-card-title>
        <v-card-text>
          {{ getTranslation('$vuetify.investorInvite.needsLogin.text') }}
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="outlined" @click="goToAuth('sign-up')">
            {{ getTranslation('$vuetify.investorInvite.needsLogin.signUp') }}
          </v-btn>
          <v-btn color="primary" class="text-white" @click="goToAuth('sign-in')">
            {{ getTranslation('$vuetify.investorInvite.needsLogin.signIn') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Personal invite: nothing to pick. The backend creates (or reuses) this person's own
           account, so the only input is the name it should carry — and only the first time. -->
      <v-card v-else-if="isPersonal" theme="dark" elevation="0" class="pa-6 invite-card">
        <v-card-title class="text-h6">
          {{ getTranslation('$vuetify.investorInvite.title', resolved.client_name) }}
        </v-card-title>
        <v-card-text>
          <p class="mb-4">{{ getTranslation('$vuetify.investorInvite.personal.description', resolved.client_name) }}</p>

          <v-text-field
            v-if="!resolved.existing_personal_client_id"
            v-model="displayName"
            variant="outlined"
            density="compact"
            :label="getTranslation('$vuetify.investorInvite.personal.nameLabel')"
            :hint="getTranslation('$vuetify.investorInvite.personal.nameHint')"
            persistent-hint
            class="mb-4"
          ></v-text-field>
          <v-alert v-else type="info" variant="tonal" density="compact" class="mb-2">
            {{ getTranslation('$vuetify.investorInvite.personal.reuseAccount') }}
          </v-alert>

          <v-alert type="info" variant="tonal" density="compact">
            {{ getTranslation('$vuetify.investorInvite.personal.modulesNotice') }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" :loading="declining" :disabled="accepting" @click="handleDecline">
            {{ getTranslation('$vuetify.investorInvite.decline') }}
          </v-btn>
          <v-btn color="primary" class="text-white" :loading="accepting" :disabled="declining" @click="handleAccept">
            {{ getTranslation('$vuetify.investorInvite.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-else-if="investorClients.length === 0" theme="dark" elevation="0" class="pa-6 invite-card">
        <v-card-title class="text-h6">
          {{ getTranslation('$vuetify.investorInvite.title', resolved.client_name) }}
        </v-card-title>
        <v-card-text>
          {{ getTranslation('$vuetify.investorInvite.needsClient.text') }}
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" :loading="declining" @click="handleDecline">
            {{ getTranslation('$vuetify.investorInvite.decline') }}
          </v-btn>
          <v-btn color="primary" class="text-white" @click="goToCreateClient">
            {{ getTranslation('$vuetify.investorInvite.needsClient.createClient') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-else theme="dark" elevation="0" class="pa-6 invite-card">
        <v-card-title class="text-h6">
          {{ getTranslation('$vuetify.investorInvite.title', resolved.client_name) }}
        </v-card-title>
        <v-card-text>
          <p class="mb-4">{{ getTranslation('$vuetify.investorInvite.description', resolved.client_name) }}</p>

          <!-- Says which of the user's accounts gets the access; with a single account there is
               nothing to pick, but the user still has to see where the invite lands. -->
          <v-alert v-if="selectedClientOption" type="info" variant="tonal" density="compact" class="mb-4">
            {{ getTranslation('$vuetify.investorInvite.grantee', selectedClientOption.client_name, resolved.client_name) }}
          </v-alert>

          <v-select
            v-if="investorClients.length > 1"
            v-model="selectedInvestorClientId"
            :items="investorClients"
            item-title="client_name"
            item-value="client_id"
            variant="outlined"
            :label="getTranslation('$vuetify.investorInvite.selectClient.label')"
            class="mb-2"
          ></v-select>

          <template v-if="selectedClientOption">
            <div v-if="selectedClientOption.existing_portfolio_company_id">
              <v-alert type="info" variant="tonal" class="mb-3">
                {{ getTranslation('$vuetify.investorInvite.existingCompany.text', resolved.client_name) }}
              </v-alert>
              <v-radio-group v-model="companyChoice">
                <v-radio :label="getTranslation('$vuetify.investorInvite.existingCompany.link')" value="existing"></v-radio>
                <v-radio :label="getTranslation('$vuetify.investorInvite.existingCompany.createNew')" value="new"></v-radio>
              </v-radio-group>
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" :loading="declining" :disabled="accepting" @click="handleDecline">
            {{ getTranslation('$vuetify.investorInvite.decline') }}
          </v-btn>
          <v-btn color="primary" class="text-white" :loading="accepting" :disabled="declining" @click="handleAccept">
            {{ getTranslation('$vuetify.investorInvite.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="5000">
        {{ snackbar.text }}
      </v-snackbar>
    </div>
  </div>
</template>

<script lang="ts">
import { gql } from '@apollo/client/core';
import { useLocale } from 'vuetify';
import { useNhostClient, useSignOut } from '@nhost/vue';

const RESOLVE_INVESTOR_INVITATION = gql`
  mutation resolveInvestorInvitation($token: uuid!) {
    resolveInvestorInvitation(token: $token) {
      valid
      reason
      client_name
      client_document
      kind
      existing_personal_client_id
      investor_clients {
        client_id
        client_name
        existing_portfolio_company_id
      }
    }
  }
`;

// Every argument but the token is nullable: a personal invite sends none of the corporate ones, and
// the handler decides what is required from the invite's own kind.
const DECLINE_INVESTOR_INVITATION = gql`
  mutation declineInvestorInvitation($token: uuid!) {
    declineInvestorInvitation(token: $token) {
      declined
    }
  }
`;

const ACCEPT_INVESTOR_INVITATION = gql`
  mutation acceptInvestorInvitation(
    $token: uuid!
    $investor_client_id: uuid
    $company_choice: String
    $portfolio_company_id: uuid
    $display_name: String
  ) {
    acceptInvestorInvitation(
      token: $token
      investor_client_id: $investor_client_id
      company_choice: $company_choice
      portfolio_company_id: $portfolio_company_id
      display_name: $display_name
    ) {
      client_id
    }
  }
`;

export default {
  name: 'AcceptInvestorInvite',

  setup() {
    const { t } = useLocale();
    // Vuetify's useLocale() adapter interpolates positionally ({0}, {1}, …), not named ({client})
    // — confirmed live (title/description came back with the placeholder untouched using named
    // interpolation). Matches the {0}/{1}/{2} style already used for Vuetify's own strings
    // (dataIterator.pageText etc.) in i18n.ts.
    const getTranslation = (path: string, ...params: string[]) => t(path, ...params);
    const { nhost } = useNhostClient();
    const { signOut } = useSignOut();
    return { getTranslation, nhost, signOut };
  },

  data() {
    return {
      loading: true,
      authenticated: false,
      resolved: {
        valid: false,
        reason: null as string | null,
        client_name: '',
        client_document: '',
        kind: null as string | null,
        existing_personal_client_id: null as string | null,
        investor_clients: null as { client_id: string, client_name: string, existing_portfolio_company_id: string | null }[] | null,
      },
      displayName: '',
      selectedInvestorClientId: null as string | null,
      companyChoice: 'existing',
      accepting: false,
      declining: false,
      snackbar: {
        show: false,
        text: '',
        color: 'error',
      },
    };
  },

  computed: {
    token(): string {
      return this.$route.params.token as string;
    },
    isPersonal(): boolean {
      return this.resolved.kind === 'personal';
    },
    investorClients() {
      return this.resolved.investor_clients ?? [];
    },
    selectedClientOption() {
      return this.investorClients.find((c: any) => c.client_id === this.selectedInvestorClientId) ?? null;
    },
    invalidReasonText() {
      const reason = this.resolved.reason ?? 'not_found';
      return this.getTranslation(`$vuetify.investorInvite.errors.${reason}`);
    },
  },

  watch: {
    selectedClientOption(option) {
      this.companyChoice = option?.existing_portfolio_company_id ? 'existing' : 'new';
    },
  },

  async mounted() {
    this.authenticated = await this.nhost.auth.isAuthenticatedAsync();

    try {
      const result = await this.$apollo.mutate({
        mutation: RESOLVE_INVESTOR_INVITATION,
        fetchPolicy: 'no-cache',
        variables: { token: this.token },
      });
      this.resolved = result.data.resolveInvestorInvitation;

      if (this.resolved.valid && this.resolved.investor_clients?.length) {
        this.selectedInvestorClientId = this.resolved.investor_clients[0].client_id;
      }

      if (this.resolved.valid && this.isPersonal && !this.resolved.existing_personal_client_id) {
        const user = this.nhost.auth.getUser();
        this.displayName = user?.displayName ?? '';
      }
    } catch (error) {
      console.error('Failed to resolve investor invitation:', error);
      this.resolved = { valid: false, reason: 'not_found', client_name: '', client_document: '', kind: null, existing_personal_client_id: null, investor_clients: null };
    } finally {
      this.loading = false;
    }
  },

  methods: {
    goToAuth(target: 'sign-in' | 'sign-up') {
      sessionStorage.setItem('investorInviteToken', this.token);
      this.$router.push(`/${target}`);
    },
    async switchAccount() {
      await this.signOut();
      this.goToAuth('sign-in');
    },
    goToCreateClient() {
      sessionStorage.setItem('investorInviteToken', this.token);
      window.location.href = '/app/create-client';
    },
    async handleDecline() {
      this.declining = true;
      try {
        await this.$apollo.mutate({
          mutation: DECLINE_INVESTOR_INVITATION,
          fetchPolicy: 'no-cache',
          variables: { token: this.token },
        });
        // The page falls back to its invalid-invite state, which now reads "você recusou este convite".
        this.resolved = { ...this.resolved, valid: false, reason: 'refused' };
      } catch (error) {
        console.error('Failed to decline investor invitation:', error);
        this.snackbar = { show: true, text: this.getTranslation('$vuetify.investorInvite.errors.declineFailed'), color: 'error' };
      } finally {
        this.declining = false;
      }
    },
    async handleAccept() {
      if (!this.isPersonal && !this.selectedInvestorClientId) return;
      this.accepting = true;
      try {
        const variables = this.isPersonal
          ? { token: this.token, display_name: this.displayName?.trim() || null }
          : {
              token: this.token,
              investor_client_id: this.selectedInvestorClientId,
              company_choice: this.companyChoice,
              portfolio_company_id: this.selectedClientOption?.existing_portfolio_company_id ?? null,
            };

        await this.$apollo.mutate({
          mutation: ACCEPT_INVESTOR_INVITATION,
          fetchPolicy: 'no-cache',
          variables,
        });

        // The new `investor` Hasura role was just granted server-side — the JWT already in the
        // browser doesn't carry it yet, so a session refresh is required before navigating into
        // the portfolio area (same gotcha documented in investor-invitation/CLAUDE.md).
        await this.nhost.auth.refreshSession();
        window.location.href = '/app/portfolio';
      } catch (error) {
        console.error('Failed to accept investor invitation:', error);
        this.snackbar = { show: true, text: this.getTranslation('$vuetify.investorInvite.errors.acceptFailed'), color: 'error' };
      } finally {
        this.accepting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.accept-invite {
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 80vh;
}

.invite-card {
  background-color: rgb(var(--v-theme-primary-800)) !important;
  border: 1px solid rgba(var(--v-theme-primary-200), 0.24);
}
</style>
